import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
} from '@mui/material';
import TemplateInputForm from '../components/AIBuilder/TemplateInputForm';
import TemplateReviewEdit from '../components/AIBuilder/TemplateReviewEdit';
import KPIConfiguration from '../components/AIBuilder/KPIConfiguration';
import ClientAccess from '../components/AIBuilder/ClientAccess';
import TemplateGenerationModal from '../components/TemplateGenerationModal';
import { aiBuilderAPI } from '../services/api';
import api from '../services/api';

interface BuilerSession {
  session_id: string;
  status: string;
  template_preview: any;
}

interface TemplateInputs {
  topic: string;
  industry: string;
  target_question_count: number;
  target_category_count: number;
  additional_context: string;
}

const AITemplateBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Input Requirements', 'Edit & Review Template', 'Configure KPIs', 'Save & Setup'];
  
  // Form and session state
  const [templateInputs, setTemplateInputs] = useState<TemplateInputs | null>(null);
  const [builderSession, setBuilderSession] = useState<BuilerSession | null>(null);
  const [configuredKPIs, setConfiguredKPIs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumeTemplateId, setResumeTemplateId] = useState<string | null>(null);
  
  // Progress modal state
  const [generationModalOpen, setGenerationModalOpen] = useState(false);
  const [generationSessionId, setGenerationSessionId] = useState<string | null>(null);

  // Handle resume parameter on mount
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId) {
      console.log('🔄 Resuming draft template:', resumeId);
      setResumeTemplateId(resumeId);
      loadDraftTemplate(resumeId);
    }
  }, [searchParams]);

  const loadDraftTemplate = async (templateId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('📥 Loading draft template...', templateId);
      
      // Load the template
      const template = await aiBuilderAPI.getTemplate(parseInt(templateId));
      
      // Load the template's questions
      const questionsData = await aiBuilderAPI.getTemplateQuestions(parseInt(templateId));
      
      // Load the template's KPIs if they exist
      try {
        const kpisResponse = await api.get(`/templates/${templateId}/kpis/`);
        if (kpisResponse.data.kpis && kpisResponse.data.kpis.length > 0) {
          const loadedKPIs = kpisResponse.data.kpis.map((kpi: any) => ({
            name: kpi.kpi_name,
            description: kpi.kpi_description
          }));
          setConfiguredKPIs(loadedKPIs);
          console.log('[OK] Loaded', loadedKPIs.length, 'KPIs for draft template');
        }
      } catch (kpiError) {
        console.log('[INFO] No KPIs found for draft template (or error loading)');
      }
      
      // Convert template + questions back to session format
      const sessionData = {
        session_id: `draft-${templateId}`, // This will be ignored by backend when existing_template_id is provided
        existing_template_id: parseInt(templateId), // Pass the actual template ID for updates
        status: 'preview',
        template_preview: {
          template_name: template.name,
          icon_name: template.icon_name,
          total_questions: questionsData.questions?.length || 0,
          structure: convertQuestionsToStructure(questionsData.questions || [])
        }
      };
      
      setBuilderSession(sessionData);
      
      // Jump to the wizard step or default to step 1 (Edit & Review)
      const targetStep = template.wizard_step || 1;
      console.log('[DEBUG] Template wizard_step from API:', template.wizard_step, 'type:', typeof template.wizard_step);
      console.log('[DEBUG] Target step after fallback:', targetStep);
      setActiveStep(targetStep);
      
      console.log('[OK] Draft template loaded, jumping to step', targetStep);
    } catch (error: any) {
      console.error('[ERROR] Error loading draft template:', error);
      setError('Failed to load draft review template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Convert flat questions array back to hierarchical structure
   */
  const convertQuestionsToStructure = (questions: any[]) => {
    const structureMap = new Map<string, any>();
    
    questions.forEach((q) => {
      const key = `${q.level01}|${q.level02}|${q.level03}`;
      
      if (!structureMap.has(key)) {
        structureMap.set(key, {
          level01: q.level01,
          level02: q.level02,
          level03: q.level03,
          questions: [],
        });
      }
      
      structureMap.get(key).questions.push({
        description: q.description,
        question_type: q.question_type,
        mandatory_beneficial: q.mandatory_beneficial,
        short_text: q.short_text,
        long_text: q.long_text,
        answer_options: q.options.map((opt: any) => ({
          option_text: opt.option_text,
          option_seq: opt.option_seq,
          score: opt.score || 0,
          action_item: opt.action_item || '',
        })),
      });
    });
    
    return Array.from(structureMap.values());
  };

  const handleInputSubmit = async (inputs: TemplateInputs) => {
    try {
      setIsLoading(true);
      setError(null);
      setTemplateInputs(inputs);

      // Open modal IMMEDIATELY (don't wait for API)
      setGenerationModalOpen(true);
      setGenerationSessionId('connecting'); // Placeholder while waiting for backend

      console.log('[AI] Starting review template generation...', inputs);
      
      const data = await aiBuilderAPI.startTemplateGeneration({
        topic: inputs.topic,
        industry: inputs.industry || undefined,
        target_question_count: inputs.target_question_count,
        target_category_count: inputs.target_category_count,
        additional_context: inputs.additional_context || undefined,
      });

      if (data.success) {
        console.log('[OK] Template generation started');
        
        // Update with real session ID
        setGenerationSessionId(data.session_id);
      } else {
        throw new Error(data.error || 'Failed to start template generation');
      }
    } catch (error: any) {
      console.error('[ERROR] Error starting template generation:', error);
      setError(error.response?.data?.error || error.message || 'Failed to start template generation. Please try again.');
      setGenerationModalOpen(false); // Close modal on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerationComplete = (template: any) => {
    console.log('[OK] Template generation complete', template);
    setBuilderSession({
      session_id: generationSessionId!,
      status: 'preview',
      template_preview: template
    });
    setGenerationModalOpen(false);
    setActiveStep(1); // Move to preview step
  };

  const handleGenerationModalClose = () => {
    // User closed modal before completion - generation continues in background
    setGenerationModalOpen(false);
  };

  const handlePreviewApprove = (editedTemplate: any) => {
    // User approves the edited template, update session and move to KPI configuration
    if (builderSession) {
      setBuilderSession({
        ...builderSession,
        template_preview: editedTemplate
      });
    }
    setActiveStep(2);
  };

  const handleKPINext = async (kpis: any[]) => {
    // Save KPIs and move to client access step
    setConfiguredKPIs(kpis);
    
    // Save draft with KPIs before moving to next step
    try {
      setIsLoading(true);
      console.log('💾 Saving draft with KPIs...', kpis.length);
      
      await aiBuilderAPI.saveTemplate({
        session_id: builderSession?.session_id || '',
        existing_template_id: (builderSession as any)?.existing_template_id,
        template_name: builderSession?.template_preview.template_name || 'Untitled Template',
        edited_structure: builderSession?.template_preview,
        client_visibility: 'none', // Will be set in final step
        kpis: kpis,
        status: 'draft',
        wizard_step: 3, // Moving to step 3 (Client Access)
      });
      
      console.log('[OK] Draft saved with KPIs');
      setActiveStep(3);
    } catch (error: any) {
      console.error('[ERROR] Error saving draft with KPIs:', error);
      // Still move to next step even if save fails
      setActiveStep(3);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPreview = () => {
    setActiveStep(1);
  };

  const handleBackToKPIs = () => {
    setActiveStep(2);
  };

  const handleFinalSave = async (finalTemplateName: string, clientVisibility: 'all' | 'selected' | 'none') => {
    // Save template with KPIs and redirect to full editor
    try {
      setIsLoading(true);
      setError(null);

      console.log('[SAVE] Saving template to database...', finalTemplateName, 'visibility:', clientVisibility, 'KPIs:', configuredKPIs.length);

      const data = await aiBuilderAPI.saveTemplate({
        session_id: builderSession?.session_id || '',
        existing_template_id: (builderSession as any)?.existing_template_id, // Pass if resuming a draft
        template_name: finalTemplateName,
        edited_structure: builderSession?.template_preview,
        client_visibility: clientVisibility,
        status: 'published',  // Mark review template as published when completing AI Builder
        kpis: configuredKPIs,
      });

      if (data.success) {
        console.log('[OK] Template saved successfully, redirecting to editor...');
        
        // Redirect to the full template editor
        navigate(`/admin/templates/edit/${data.template_id}`, { 
          state: { 
            message: `Template "${finalTemplateName}" created successfully with ${configuredKPIs.length} KPIs! You can now edit questions and customize everything.`,
            templateId: data.template_id,
            fromAIBuilder: true,
            showKPITab: true
          }
        });
      } else {
        throw new Error(data.error || 'Failed to save review template');
      }
    } catch (error: any) {
      console.error('[ERROR] Error saving template:', error);
      setError(error.response?.data?.error || error.message || 'Failed to save template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateTemplate = () => {
    // User wants to regenerate with same inputs
    if (templateInputs) {
      handleInputSubmit(templateInputs);
    }
  };

  const handleBackToRequirements = () => {
    // User wants to go back and change their inputs
    setActiveStep(0);
    // Reset the builder session to allow new generation
    setBuilderSession(null);
  };


  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <TemplateInputForm
            onSubmit={handleInputSubmit}
            isLoading={isLoading}
            error={error}
            initialData={templateInputs}
          />
        );
      case 1:
        return builderSession ? (
          <TemplateReviewEdit
            session={builderSession}
            onApprove={handlePreviewApprove}
            onRegenerate={handleRegenerateTemplate}
            onBack={handleBackToRequirements}
            isLoading={isLoading}
            error={error}
          />
        ) : null;
      case 2:
        return builderSession ? (
          <KPIConfiguration
            templateName={builderSession.template_preview.template_name}
            templateStructure={builderSession.template_preview}
            initialKPIs={configuredKPIs.length > 0 ? configuredKPIs : undefined}
            onNext={handleKPINext}
            onBack={handleBackToPreview}
            isLoading={isLoading}
            error={error}
          />
        ) : null;
      case 3:
        return builderSession ? (
          <ClientAccess
            templateName={builderSession.template_preview.template_name}
            kpiCount={configuredKPIs.length}
            onSave={handleFinalSave}
            onBack={handleBackToKPIs}
            isLoading={isLoading}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          🤖 AI Template Builder
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Create professional audit templates with AI assistance
        </Typography>
      </Box>

      {/* Progress Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Global Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Paper sx={{ p: 4 }}>
        {renderStepContent()}
      </Paper>

      {/* Generation Progress Modal */}
      <TemplateGenerationModal
        open={generationModalOpen}
        sessionId={generationSessionId}
        onClose={handleGenerationModalClose}
        onComplete={handleGenerationComplete}
      />
    </Container>
  );
};

export default AITemplateBuilder; 